import {
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ControlConfig } from '../../../models/api/reports/report';
import { ControlDependencyService } from '../../../services/reports/control-dependency.service';
import { DisplayService } from '../../../services/reports/display.service';
import { ProcessedControl } from '../../../services/reports/reports.service';
import { ValidatorsEx } from '../../../validators/validators-ex';
import { InputComponent } from '../../core/controls/input.component';
import { SelectComponent } from '../../core/controls/select.component';
import { DatePickerFieldComponent } from '../../core/fields/date-picker-field.component';
import { FieldContainerComponent } from '../../core/fields/field-container.component';
import { InputFieldComponent } from '../../core/fields/input-field.component';
import { RadioButtonFieldComponent } from '../../core/fields/radio-button-field.component';
import { RangeFieldComponent } from '../../core/fields/range-field.component';
import { SelectFieldComponent } from '../../core/fields/select-field.component';
import { PageHeaderComponent } from '../../core/labels/page-header.component';
import { BankAccountCriteriaComponent } from './bank-account-criteria.component';
import { BaseCriteriaComponent } from './base-criteria.component';
import { DocumentCriteriaComponent } from './document-criteria.component';
import { DyoFinancialFieldsComponent } from './dyo-financial-fields.component';
import { DyoNonFinancialFieldsComponent } from './dyo-non-financial-fields.component';
import { FinancialCriteriaComponent } from './financial-criteria.component';
import { PaycodeCriteriaComponent } from './paycode-criteria.component';
import { ReserveCriteriaComponent } from './reserve-criteria.component';
import { SharedControlConfigs } from './shared-control-configs';

interface SelectionCriteriaFormControls {
  asOfDate?: FormControl<Date | null>;
  comparedWithAsOfDate?: FormControl<Date | null>;
  transactionFromDate?: FormControl<Date | null>;
  transactionToDate?: FormControl<Date | null>;
  payType?: FormControl<string | null>;
  toDays1?: FormControl<number | null>;
  toDays2?: FormControl<number | null>;
  toDays3?: FormControl<number | null>;
  toDays4?: FormControl<number | null>;
  toDays5?: FormControl<number | null>;
  toDays6?: FormControl<number | null>;
  toDays7?: FormControl<number | null>;
  toDays8?: FormControl<number | null>;
  lagTimeColumnA?: FormControl<string | null>;
  lagTimeColumnB?: FormControl<string | null>;
  paymentType?: FormControl<string | string[] | null>;
  documentType?: FormControl<string[] | null>;
  payeeName?: FormControl<string | null>;
  incurredChangeGreater?: FormControl<number | null>;
  paidChangeGreater?: FormControl<number | null>;
  repeatDetection?: FormControl<string | null>;
  clearedStatus?: FormControl<string | null>;
  lossSizeBoundary1?: FormControl<number | null>;
  lossSizeBoundary2?: FormControl<number | null>;
  lossSizeBoundary3?: FormControl<number | null>;
  lossSizeBoundary4?: FormControl<number | null>;
  lossSizeBoundary5?: FormControl<number | null>;
  lossSizeBoundary6?: FormControl<number | null>;
  lossSizeBoundary7?: FormControl<number | null>;
  lossSizeBoundary8?: FormControl<number | null>;
  lossPaymentType?: FormControl<string | null>;
  severityPaymentType?: FormControl<string | null>;
  includeAtUnitLevel?: FormControl<string[] | null>;
}

@Component({
  selector: 'app-selection-criteria',
  imports: [
    DatePickerFieldComponent,
    RadioButtonFieldComponent,
    PageHeaderComponent,
    SelectFieldComponent,
    RangeFieldComponent,
    InputFieldComponent,
    DocumentCriteriaComponent,
    PaycodeCriteriaComponent,
    BankAccountCriteriaComponent,
    FinancialCriteriaComponent,
    ReserveCriteriaComponent,
    FieldContainerComponent,
    InputComponent,
    SelectComponent,
    DyoFinancialFieldsComponent,
    DyoNonFinancialFieldsComponent,
  ],
  template: `
    <div class="tw-flex tw-flex-col tw-w-full tw-gap-2">
      <app-page-header label="Selection criteria" headerSize="h3" />

      @if (showTransactionDateRange()) {
        <app-range-field
          [label]="transactionDateLabel"
          valueClasses="tw-max-w-[400px]"
          [control]="fg.controls.transactionFromDate"
          [toControl]="fg.controls.transactionToDate"
          [compact]="true"
          type="Date"
          [requireBoth]="true"
          [maxSpanDays]="null"
        />
      }
      @if (showComparedWithAsOfDate() && showAsOfDate()) {
        <!-- Comparative reports: both dates exist, use range for automatic validation -->
        <app-range-field
          label="Comparative dates"
          valueClasses="tw-max-w-[400px]"
          [control]="fg.controls.comparedWithAsOfDate"
          [toControl]="fg.controls.asOfDate"
          fromLabel="Compared with as of date"
          toLabel="As of date"
          [compact]="true"
          type="Date"
          [requireBoth]="true"
          [reverseOrder]="true"
          dateLessThanMessage="Compared with as of date must be earlier than As of date"
          dateGreaterThanMessage="As of date must be later than Compared with as of date"
          [maxSpanDays]="null"
        />
      } @else if (showAsOfDate()) {
        <!-- Standalone As of date (may be read-only for some reports) -->
        <app-date-picker-field
          [control]="fg.controls.asOfDate"
          [label]="getLabel('AS_OF_DATE') || 'As of date'"
          [disabled]="isAsOfDateDisabled()"
        />
      }
      @if (showPaymentTypes()) {
        <app-select-field
          [label]="
            getLabelFromAny('PAYMENT_TYPES', 'INCLUDE_THESE_PAYMENT_TYPES')
          "
          optionLabel="name"
          optionValue="code"
          [virtualScroll]="false"
          [showHeader]="true"
          [multiple]="isPaymentTypesMultiple()"
          [showClear]="false"
          [filter]="false"
          [multipleFilter]="false"
          [options]="paymentTypesOptions()"
          [control]="fg.controls.paymentType"
          [maxSelectedLabels]="3"
          valueClasses="tw-max-w-[400px]"
        />
      }
      @if (showPaymentStatus()) {
        <app-select-field
          [label]="getLabel('INCLUDE_VOIDS')"
          optionLabel="name"
          optionValue="code"
          [virtualScroll]="false"
          [showHeader]="false"
          [filter]="false"
          [multiple]="false"
          [showClear]="false"
          [options]="paymentStatusOptions()"
          [control]="fg.controls.clearedStatus"
          valueClasses="tw-max-w-[400px]"
        />
      }
      @if (showPayeeNameInHeader()) {
        <app-input-field
          [control]="fg.controls.payeeName"
          [label]="getLabel('PAYEE_NAME')"
          valueClasses="tw-max-w-[500px]"
        />
      }
      @if (showDocumentTypes()) {
        <app-select-field
          [label]="
            getLabelFromAny(
              'INCLUDE_THESE_DOCUMENT_TYPES',
              'INCLUDE_DOCUMENT_TYPES_AUTH',
              'DOCUMENT_TYPE'
            )
          "
          optionLabel="name"
          optionValue="code"
          [virtualScroll]="false"
          [showHeader]="true"
          [multiple]="true"
          [showClear]="true"
          [options]="documentTypesOptions()"
          [control]="fg.controls.documentType"
          valueClasses="tw-max-w-[400px]"
        />
      }
      @if (showIncludeAtUnitLevel()) {
        <app-select-field
          [control]="fg.controls.includeAtUnitLevel"
          [label]="getLabel('INCLUDE_AT_UNIT_LEVEL')"
          [options]="includeAtUnitLevelOptions()"
          optionLabel="name"
          optionValue="code"
          [multiple]="true"
          [filter]="false"
          [showClear]="true"
          valueClasses="tw-max-w-[400px]"
        />
      }
      @if (showGrossNetPaidType() && isGrossNetPaidTypeVisible()) {
        <app-radio-button-field
          [control]="fg.controls.payType"
          optionLabel="name"
          optionValue="code"
          [options]="grossNetPaidTypeOptions()"
          [showHelpText]="false"
          [vertical]="false"
          [label]="getLabel('GROSS_NET_PAID_TYPE')"
          valueClasses="tw-min-w-[200px]"
        />
      }
      @if (showIncurredChangeGreater()) {
        <app-input-field
          [control]="fg.controls.incurredChangeGreater"
          [label]="getLabel('INCURRED_CHANGE_GREATER')"
          type="Currency"
          valueClasses="tw-max-w-[150px]"
        />
      }
      @if (showPaidChangeGreater()) {
        <app-input-field
          [control]="fg.controls.paidChangeGreater"
          [label]="getLabel('PAID_CHANGE_GREATER')"
          type="Currency"
          valueClasses="tw-max-w-[150px]"
        />
      }
      @if (showRepeatDetection()) {
        <app-radio-button-field
          [control]="fg.controls.repeatDetection"
          optionLabel="name"
          optionValue="code"
          [options]="repeatDetectionOptions()"
          [showHelpText]="false"
          [vertical]="false"
          [label]="getLabel('REPEAT_DETECTION')"
          valueClasses="tw-min-w-[200px]"
        />
      }
      @if (showLossSizeBoundary()) {
        <app-field-container label="Upto $ : ">
          <ng-template #value>
            <div class="tw-flex tw-flex-col tw-gap-2">
              @let items =
                [
                  fg.controls.lossSizeBoundary1,
                  fg.controls.lossSizeBoundary2,
                  fg.controls.lossSizeBoundary3,
                  fg.controls.lossSizeBoundary4,
                  fg.controls.lossSizeBoundary5,
                  fg.controls.lossSizeBoundary6,
                  fg.controls.lossSizeBoundary7,
                  fg.controls.lossSizeBoundary8,
                ];
              @for (item of items; track item; let i = $index) {
                <app-input
                  [control]="item"
                  [label]="
                    getLabel('LOSS_SIZE_BOUNDARY_' + (i + 1)) ??
                    (i === 0 ? 'Up to' : 'Then to')
                  "
                  type="Currency"
                  placeholder="$"
                  classes="tw-max-w-[120px]"
                />
              }
            </div>
          </ng-template>
        </app-field-container>
      }
      @if (showLossPaymentType()) {
        <app-radio-button-field
          [control]="fg.controls.lossPaymentType"
          optionLabel="name"
          optionValue="code"
          [options]="lossPaymentTypeOptions()"
          [showHelpText]="false"
          [vertical]="false"
          [label]="getLabel('LOSS_PAYMENT_TYPE')"
          valueClasses="tw-min-w-[200px]"
        />
      }
      @if (showSeverityPaymentType()) {
        <app-radio-button-field
          [control]="fg.controls.severityPaymentType"
          optionLabel="name"
          optionValue="code"
          [options]="severityPaymentTypeOptions()"
          [showHelpText]="false"
          [vertical]="false"
          [label]="getLabel('SEVERITY_PAYMENT_TYPE')"
          valueClasses="tw-min-w-[200px]"
        />
      }
      @if (showToDays()) {
        <app-field-container label="Upto (days) : ">
          <ng-template #value>
            <div class="tw-flex tw-flex-col tw-gap-2">
              @let items =
                [
                  fg.controls.toDays1,
                  fg.controls.toDays2,
                  fg.controls.toDays3,
                  fg.controls.toDays4,
                  fg.controls.toDays5,
                  fg.controls.toDays6,
                  fg.controls.toDays7,
                  fg.controls.toDays8,
                ];
              @for (item of items; track item; let i = $index) {
                <app-input
                  [control]="item"
                  [label]="
                    getLabel('LAGTIME_BOUNDARY_' + (i + 1)) ??
                    (i === 0 ? 'Up to' : 'Then to')
                  "
                  type="Integer"
                  placeholder="Days"
                  classes="tw-max-w-[120px]"
                />
              }
            </div>
          </ng-template>
        </app-field-container>
      }
      @if (showLagTimeBetween()) {
        <app-field-container label="Lag time between">
          <ng-template #value>
            <div class="tw-flex">
              <app-select
                [control]="fg.controls.lagTimeColumnA"
                label="Column A"
                optionLabel="name"
                optionValue="code"
                [options]="lagTimeColumnOptions()"
                [virtualScroll]="false"
                [showClear]="false"
                [filter]="false"
                classes="tw-min-w-[200px]"
              />
              <app-select
                [control]="fg.controls.lagTimeColumnB"
                label="Column B"
                optionLabel="name"
                optionValue="code"
                [options]="lagTimeColumnOptions()"
                [virtualScroll]="false"
                [showClear]="false"
                [filter]="false"
                classes="tw-min-w-[200px]"
              />
            </div>
          </ng-template>
        </app-field-container>
      }
      @if (display.showFinancialCriteria()) {
        <app-financial-criteria #financialCriteria />
      }
      @if (display.showDyoNonFinancialFields()) {
        <app-dyo-non-financial-fields #dyoNonFinancialFields />
      }
      @if (display.showDyoFinancialFields()) {
        <app-dyo-financial-fields #dyoFinancialFields />
      }
      @if (display.showDocumentCriteria()) {
        <app-document-criteria #documentCriteria />
      }
      @if (display.showPaycodeCriteria()) {
        <app-paycode-criteria #paycodeCriteria />
      }
      @if (display.showBankAccountCriteria()) {
        <app-bank-account-criteria #bankAccountCriteria />
      }
      @if (display.showReserveCriteria()) {
        <app-reserve-criteria #reserveCriteria />
      }
    </div>
  `,
})
export class SelectionCriteriaComponent
  extends BaseCriteriaComponent
  implements OnInit, OnDestroy
{
  protected display = inject(DisplayService);
  protected controlDependency = inject(ControlDependencyService);

  @ViewChild('financialCriteria')
  financialCriteria: FinancialCriteriaComponent;

  @ViewChild('documentCriteria')
  documentCriteria: DocumentCriteriaComponent;

  @ViewChild('paycodeCriteria')
  paycodeCriteria: PaycodeCriteriaComponent;

  @ViewChild('bankAccountCriteria')
  bankAccountCriteria: BankAccountCriteriaComponent;

  @ViewChild('reserveCriteria')
  reserveCriteria: ReserveCriteriaComponent;

  @ViewChild('dyoFinancialFields')
  dyoFinancialFields?: DyoFinancialFieldsComponent;

  @ViewChild('dyoNonFinancialFields')
  dyoNonFinancialFields?: DyoNonFinancialFieldsComponent;

  showAsOfDate = signal(false);
  showComparedWithAsOfDate = signal(false);
  showTransactionDateRange = signal(false);
  showPaymentTypes = signal(false);
  showDocumentTypes = signal(false);
  showPaymentStatus = signal(false);
  showPayeeNameInHeader = signal(false);
  showIncurredChangeGreater = signal(false);
  showPaidChangeGreater = signal(false);
  showRepeatDetection = signal(false);
  showGrossNetPaidType = signal(false);
  showIncludeAtUnitLevel = signal(false);
  showLossSizeBoundary = signal(false);
  showLossPaymentType = signal(false);
  showSeverityPaymentType = signal(false);
  showToDays = signal(false);
  showLagTimeBetween = signal(false);

  transactionDateLabel = 'Transaction date';

  // Computed signals for template options
  paymentTypesOptions = computed(() =>
    this.getOptionsFromAny('PAYMENT_TYPES', 'INCLUDE_THESE_PAYMENT_TYPES'),
  );

  documentTypesOptions = computed(() =>
    this.getOptionsFromAny(
      'INCLUDE_THESE_DOCUMENT_TYPES',
      'INCLUDE_DOCUMENT_TYPES_AUTH',
      'DOCUMENT_TYPE',
    ),
  );

  paymentStatusOptions = computed(() =>
    this.controlConfig
      .getControlOptions('INCLUDE_VOIDS')
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  grossNetPaidTypeOptions = computed(() =>
    this.controlConfig
      .getControlOptions('GROSS_NET_PAID_TYPE')
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  repeatDetectionOptions = computed(() =>
    this.controlConfig
      .getControlOptions('REPEAT_DETECTION')
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  lossPaymentTypeOptions = computed(() =>
    this.controlConfig
      .getControlOptions('LOSS_PAYMENT_TYPE')
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  severityPaymentTypeOptions = computed(() =>
    this.controlConfig
      .getControlOptions('SEVERITY_PAYMENT_TYPE')
      .sort((a, b) => a.name.localeCompare(b.name)),
  );

  lagTimeColumnOptions = computed(() =>
    this.getOptionsFromAny('LAG_TIME_CALC_DATE_1', 'LAG_TIME_CALC_DATE_2').sort(
      (a, b) => a.name.localeCompare(b.name),
    ),
  );

  includeAtUnitLevelOptions = computed(() =>
    this.controlConfig.getControlOptions('INCLUDE_AT_UNIT_LEVEL'),
  );

  protected readonly regionName: string = null;

  protected CONTROL_CONFIGS: ControlConfig[] = [
    SharedControlConfigs.createAsOfDateConfig(this.showAsOfDate),
    SharedControlConfigs.createComparedWithAsOfDateConfig(
      this.showComparedWithAsOfDate,
    ),
    ...SharedControlConfigs.createTransactionDateRangeConfigs(
      this.showTransactionDateRange,
      this.showTransactionDateRange,
    ),
    SharedControlConfigs.createPaymentTypeConfig(this.showPaymentTypes),
    SharedControlConfigs.createDocumentTypeConfig(this.showDocumentTypes),
    SharedControlConfigs.createPaymentStatusConfig(this.showPaymentStatus),
    SharedControlConfigs.createPayeeNameConfig(this.showPayeeNameInHeader),
    SharedControlConfigs.createCurrencyConfig(
      'INCURRED_CHANGE_GREATER',
      'incurredChangeGreater',
      this.showIncurredChangeGreater,
    ),
    SharedControlConfigs.createCurrencyConfig(
      'PAID_CHANGE_GREATER',
      'paidChangeGreater',
      this.showPaidChangeGreater,
    ),
    {
      ...SharedControlConfigs.createRadioButtonConfig(
        'REPEAT_DETECTION',
        'repeatDetection',
        this.showRepeatDetection,
      ),
      defaultOptionCode: 'SSN',
    },
    SharedControlConfigs.createRadioButtonConfig(
      'GROSS_NET_PAID_TYPE',
      'payType',
      this.showGrossNetPaidType,
    ),
    SharedControlConfigs.createIncludeAtUnitLevelConfig(
      this.showIncludeAtUnitLevel,
    ),
    {
      ...SharedControlConfigs.createRadioButtonConfig(
        'LOSS_PAYMENT_TYPE',
        'lossPaymentType',
        this.showLossPaymentType,
      ),
      defaultOptionCode: 'O',
    },
    SharedControlConfigs.createRadioButtonConfig(
      'SEVERITY_PAYMENT_TYPE',
      'severityPaymentType',
      this.showSeverityPaymentType,
    ),
    ...Array.from({ length: 8 }, (_, i) =>
      SharedControlConfigs.createCurrencyConfig(
        `LOSS_SIZE_BOUNDARY_${i + 1}`,
        `lossSizeBoundary${i + 1}`,
        this.showLossSizeBoundary,
      ),
    ),
    ...Array.from({ length: 8 }, (_, i) => ({
      ...SharedControlConfigs.createNumberConfig(
        `LAGTIME_BOUNDARY_${i + 1}`,
        `toDays${i + 1}`,
        this.showToDays,
      ),
      ...(i > 0 ? { required: false } : {}),
    })),
    {
      erfNames: 'LAG_TIME_CALC_DATE_1',
      formControlName: 'lagTimeColumnA',
      visibility: this.showLagTimeBetween,
      defaultToFirstOption: true,
    },
    {
      erfNames: 'LAG_TIME_CALC_DATE_2',
      formControlName: 'lagTimeColumnB',
      visibility: this.showLagTimeBetween,
      defaultToFirstOption: true,
    },
  ];

  fg = new FormGroup<SelectionCriteriaFormControls>({
    // Controls are created dynamically by control-configuration.service
  });

  override get invalid(): boolean {
    return (
      this.fg.invalid ||
      (this.display.showFinancialCriteria() &&
        this.financialCriteria?.invalid) ||
      (this.display.showDyoFinancialFields() &&
        this.dyoFinancialFields?.invalid) ||
      (this.display.showDocumentCriteria() && this.documentCriteria?.invalid) ||
      (this.display.showPaycodeCriteria() && this.paycodeCriteria?.invalid) ||
      (this.display.showBankAccountCriteria() &&
        this.bankAccountCriteria?.invalid) ||
      (this.display.showReserveCriteria() && this.reserveCriteria?.invalid)
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setupDependencyWatchers();
    this.applyUserLoginCountValidations();
    this.applyLagTimeBoundaryValidations();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.controlDependency.clearSubscriptions();
  }

  private setupDependencyWatchers(): void {
    // Setup dependency watchers for controls that have visibleWhen/disabledWhen conditions
    this.controlDependency.setupDependencyWatchers(this.fg, [
      'AS_OF_DATE',
      'GROSS_NET_PAID_TYPE',
    ]);
  }

  private applyUserLoginCountValidations(): void {
    const reportId = this.reports.currentReport()?.reportTemplate?.id;
    if (reportId !== 'USER_LOGIN_COUNT') return;

    const controls = [
      this.fg.controls.transactionFromDate,
      this.fg.controls.transactionToDate,
      this.fg.controls.asOfDate,
    ];

    for (const control of controls) {
      if (control) {
        control.addValidators([
          ValidatorsEx.dateNotFuture(),
          ValidatorsEx.dateMaxYearsAgo(2),
        ]);
        control.updateValueAndValidity({ emitEvent: false });
      }
    }
  }

  private applyLagTimeBoundaryValidations(): void {
    const controls = [
      this.fg.controls.toDays1,
      this.fg.controls.toDays2,
      this.fg.controls.toDays3,
      this.fg.controls.toDays4,
      this.fg.controls.toDays5,
      this.fg.controls.toDays6,
      this.fg.controls.toDays7,
      this.fg.controls.toDays8,
    ].filter((c): c is FormControl<number | null> => c != null);

    if (controls.length < 2) return;

    for (let i = 1; i < controls.length; i++) {
      controls[i].addValidators([
        ValidatorsEx.consecutiveBoundary(() => controls, i),
        ValidatorsEx.increasingBoundary(() => controls[i - 1]),
      ]);
      controls[i].updateValueAndValidity({ emitEvent: false });
    }

    for (const ctrl of controls) {
      this.dispose.subs = ctrl.valueChanges.subscribe(() => {
        for (const sibling of controls) {
          if (sibling !== ctrl) {
            sibling.updateValueAndValidity({ emitEvent: false });
          }
        }
      });
    }
  }

  isAsOfDateDisabled(): boolean {
    const control = this.controlConfig.getControl('AS_OF_DATE');
    return control?.disabled ?? false;
  }

  isGrossNetPaidTypeVisible(): boolean {
    return this.controlDependency.isVisible('GROSS_NET_PAID_TYPE')();
  }

  protected override storeControlReference(
    config: ControlConfig,
    control: ProcessedControl,
  ): void {
    const erfName = Array.isArray(config.erfNames)
      ? config.erfNames[0]
      : config.erfNames;

    if (erfName === 'TRANSACTION_FROM_DATE' && control.label) {
      const newLabel = control.label
        .replace(/\s*From\s+Date\s*$/i, ' date')
        .trim();
      this.transactionDateLabel = newLabel || 'Transaction date';
    }
  }

  reloadChildComponents() {
    Promise.resolve().then(() => {
      if (this.display.showFinancialCriteria() && this.financialCriteria) {
        this.financialCriteria.loadAllConfigurations();
      }
      if (this.display.showDocumentCriteria() && this.documentCriteria) {
        this.documentCriteria.loadAllConfigurations();
      }
      if (this.display.showPaycodeCriteria() && this.paycodeCriteria) {
        this.paycodeCriteria.loadAllConfigurations();
      }
      if (this.display.showBankAccountCriteria() && this.bankAccountCriteria) {
        this.bankAccountCriteria.loadAllConfigurations();
      }
      if (this.display.showDyoFinancialFields() && this.dyoFinancialFields) {
        this.dyoFinancialFields.reload();
      }
      if (
        this.display.showDyoNonFinancialFields() &&
        this.dyoNonFinancialFields
      ) {
        this.dyoNonFinancialFields.reload();
      }
    });
  }

  isPaymentTypesMultiple(): boolean {
    const paymentTypesControl = this.getControlFromAny(
      'PAYMENT_TYPES',
      'INCLUDE_THESE_PAYMENT_TYPES',
    );
    return paymentTypesControl?.displayType === 'CHECKBOXLIST';
  }

  protected getOptionsFromAny(
    ...erfNames: string[]
  ): { name: string; code: string }[] {
    for (const erfName of erfNames) {
      const options = this.controlConfig.getControlOptions(erfName);
      if (options.length > 0) {
        return options;
      }
    }
    return [];
  }

  protected getControlFromAny(
    ...erfNames: string[]
  ): ProcessedControl | undefined {
    for (const erfName of erfNames) {
      const control = this.controlConfig.getControl(erfName);
      if (control) {
        return control;
      }
    }
    return undefined;
  }

  protected getLabelFromAny(...erfNames: string[]): string | undefined {
    for (const erfName of erfNames) {
      const label = this.controlConfig.getControlLabel(erfName);
      if (label) {
        return label;
      }
    }
    return undefined;
  }

  protected getLabel(erfName: string): string | undefined {
    return this.controlConfig.getControlLabel(erfName);
  }

  protected override updateChildComponents(): void {
    if (this.display.showFinancialCriteria() && this.financialCriteria) {
      this.financialCriteria.updateReport();
    }
    if (this.display.showDocumentCriteria() && this.documentCriteria) {
      this.documentCriteria.updateReport();
    }
    if (this.display.showPaycodeCriteria() && this.paycodeCriteria) {
      this.paycodeCriteria.updateReport();
    }
    if (this.display.showBankAccountCriteria() && this.bankAccountCriteria) {
      this.bankAccountCriteria.updateReport();
    }
    if (this.display.showReserveCriteria() && this.reserveCriteria) {
      this.reserveCriteria.updateReport();
    }
    if (this.display.showDyoFinancialFields() && this.dyoFinancialFields) {
      this.dyoFinancialFields.updateReport();
    }
    if (
      this.display.showDyoNonFinancialFields() &&
      this.dyoNonFinancialFields
    ) {
      this.dyoNonFinancialFields.updateReport();
    }
  }
}
